import gql from 'graphql-tag';
import React from 'react';

import {assert} from 'Asserts';
import {Permissions} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import {STATUS} from 'constants/StatusConstants';
import {User} from 'constants/UserConstants';
import {ReviewerStatus, ReviewLifecycleActionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import ConfigureReviewCriteriaSection from 'notebook/reviews/ConfigureReviewCriteriaSection';
import CreateUpdateReviewModalClass from 'notebook/reviews/CreateUpdateReviewModal';
import NoReviewInProgress from 'notebook/reviews/NoReviewInProgress';
import ReviewConstants, {
  COMMENT as REVIEW_CONSTANTS_COMMENT_ICON,
  HistoryItem,
  Review as ReviewModel,
  ReviewerUser,
} from 'notebook/reviews/ReviewConstants';
import ReviewCriteriaSection from 'notebook/reviews/ReviewCriteriaSection';
import ReviewLabelText from 'notebook/reviews/ReviewLabelText';
import SnapshotInfo, {LastSnapshotFailedIconAndSummary} from 'notebook/reviews/SnapshotInfo';
import getTestProps from 'test/lib/cypress/getTestProps';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {RelativeTime} from 'ui-library/Times';
import APIUtil from 'util/APIUtil';
import {typeToName} from 'util/NameUtil';
import {OngoingTaskResponse} from 'util/TaskRunner';
import {assertNever} from 'util/Types';

import {
  ReviewLifecycleReviewersQuery,
  ReviewLifecycleReviewersQueryVariables,
} from './__generated/ReviewLifecycleReviewersQuery';
import {REVIEW_ACTION_TO_DISPLAY_PAST_TENSE} from './review-lifecycles/ReviewLifecycleConstants';

export const REVIEW_LIFECYCLE_REVIEWERS_QUERY = gql`
  query ReviewLifecycleReviewersQuery($reviewId: StrictString!) @skipAudit @engComponent(name: NOTEBOOK) {
    review(id: $reviewId) {
      ... on ReviewNode {
        id
        reviewers {
          ... on ReviewerNode {
            id
            isUserValidReviewer
            reviewStatus
            user {
              ... on UserNode {
                id
                name
                email
                handle
              }
            }
          }
        }
        lastUsedReviewLifecycleVersion {
          id
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }

  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface CheckboxProps {
  // The tooltip title to use when hovering over a Checkbox
  title: string;
}

/**
 * Our checkboxes are used to show when a Reviewer has approved or not approved
 * an entry as part of a review.  The title provided in the props is used as the
 * tooltip when hovering over the icon.
 */
export function ApprovedCheckbox({title}: CheckboxProps): JSX.Element {
  return (
    <Tooltip text={title} placement={FloatingPlacement.BOTTOM}>
      <BenchlingIcon className="u-success-color" icon="square-check-outline" />
    </Tooltip>
  );
}

export function UnapprovedCheckbox({title}: CheckboxProps): JSX.Element {
  return (
    <Tooltip text={title} placement={FloatingPlacement.BOTTOM}>
      <BenchlingIcon icon="square-outline" />
    </Tooltip>
  );
}

interface ReviewProps {
  // The id of the entry whose review state is being show
  id: string;

  // The Review backend model for the entry
  review?: ReviewModel;

  // How many review actions should we shown initially, defaults to 3
  initialItemsShowing: number;

  // A callback which will be called after we submit a change to the
  // review object but before we close the CreateUpdateReviewModal.
  // TODO(TypeScript) - the filePermissions below needs to match ReviewDAO.updateReview's return type
  onUpdate: (
    fileId: string,
    review: ReviewModel,
    filePermissions: Permissions,
    snapshotTask: OngoingTaskResponse | null
  ) => void;

  // Permissions for the current viewer on the entry. `writable` will be true if the user can modify auditors
  // assigned to a non-RLC entry and configure review criteria.
  // `canSendForReview` will be true if the user currently viewing the
  // component can add criteria groups to the review
  permissions: {writable: boolean; canSendForReview?: boolean};
  hasReviewLifecycle?: boolean;
}

interface ReviewState {
  historyStartIndex: number;
}

/**
 * Return the appropriate BenchlingIcon for the action taking as
 * part of the historyItem.
 *
 * @param historyItem - contains the action which will either be UPDATE or REVIEW
 * @param className - a CSS class to give to the icon
 * @returns a JSX.Element containing the icon
 * @throws an Error if the action of the historyItem is unknown
 */
function HistoryItemIcon({
  historyItem,
  className,
}: {
  historyItem: HistoryItem;
  className: string;
}): JSX.Element {
  switch (historyItem.action) {
    case 'UPDATE':
      return <BenchlingIcon icon="edit" className={className} />;
    case 'REVIEW':
      return (
        <BenchlingIcon
          icon={historyItem.info.new_status || REVIEW_CONSTANTS_COMMENT_ICON}
          className={className}
        />
      );
    default:
      throw new Error(`Unexpected action ${assertNever(historyItem).action}`);
  }
}

/**
 * Return a React element describing what action was taken as part of this historyItem.
 *
 * @param historyItem -- the history entry to be shown
 * @param apiType -- a string representation of the api type of item that the
 *                   historyItem belongs to, e.g. ModelTypeConstants.ENTRY or ModelTypeConstants.STAGE_ENTRY
 * @returns a React element that displays a description of the action performed
 */
function HistoryItemSummary({
  apiType,
  historyItem,
  hasReviewLifecycle,
}: {
  apiType: string;
  historyItem: HistoryItem;
  hasReviewLifecycle?: boolean;
}): JSX.Element {
  const typeName = typeToName(apiType);
  const userName = historyItem.user.name;

  switch (historyItem.action) {
    case 'UPDATE':
      return (
        <>
          <strong>{userName}</strong> made changes to this {typeName}.
        </>
      );
    case 'REVIEW': {
      const historyItemAction = historyItem.reviewLifecycleInfo?.reviewStageInfo?.action;
      const action =
        REVIEW_ACTION_TO_DISPLAY_PAST_TENSE[
          historyItemAction ?? ReviewLifecycleActionType.APPROVE
        ].toLowerCase();
      const stageName = historyItem.reviewLifecycleInfo?.reviewStageInfo?.name;

      switch (historyItem.info.new_status) {
        case ReviewConstants.REJECTED:
          if (historyItemAction && stageName) {
            return (
              <>
                <strong>{userName}</strong> requested changes to this {typeName} for stage "{stageName}".
              </>
            );
          }
          return (
            <>
              <strong>{userName}</strong> requested changes to this {typeName}.
            </>
          );
        case ReviewConstants.REVIEW_SNAPSHOT_IN_PROGRESS:
          if (historyItemAction && stageName) {
            return (
              <>
                <strong>{userName}</strong> {action} stage "{stageName}" of this {typeName} (pending
                snapshot).
              </>
            );
          }
          return (
            <>
              <strong>{userName}</strong> sent this {typeName} for {hasReviewLifecycle ? '' : 'auditor '}
              review (pending snapshot).
            </>
          );
        case ReviewConstants.NEEDS_REVIEW:
          if (historyItem.info.wasCreatedBySnapshot) {
            return <>This {typeName} was marked as ready for review.</>;
          } else if (historyItemAction && stageName) {
            return (
              <>
                <strong>{userName}</strong> {action} stage "{stageName}" of this {typeName}.
              </>
            );
          } else {
            return (
              <>
                <strong>{userName}</strong> sent this {typeName} for {hasReviewLifecycle ? '' : 'auditor '}
                review.
              </>
            );
          }
        case ReviewConstants.ACCEPTANCE_SNAPSHOT_IN_PROGRESS:
          if (historyItemAction && stageName) {
            return (
              <>
                <strong>{userName}</strong> {action} stage "{stageName}" of this {typeName} (pending
                snapshot).
              </>
            );
          }
          return (
            <>
              <strong>{userName}</strong> {action} this {typeName} (pending snapshot).
            </>
          );
        case ReviewConstants.ACCEPTED:
          if (historyItem.info.wasCreatedBySnapshot) {
            return <>This {typeName} was marked as accepted.</>;
          } else if (historyItemAction && stageName) {
            return (
              <>
                <strong>{userName}</strong> {action} stage "{stageName}" of this {typeName}.
              </>
            );
          } else {
            return (
              <>
                <strong>{userName}</strong> {action} this {typeName}.
              </>
            );
          }
        case ReviewConstants.RETRACTED:
          return (
            <>
              <strong>{userName}</strong> retracted this {typeName}.
            </>
          );
        case null:
          return (
            <>
              <strong>{userName}</strong> made a comment.
            </>
          );
        default:
          throw new Error(`Unexpected status ${historyItem.info.new_status}`);
      }
    }
    default:
      throw new Error(`Unexpected action ${assertNever(historyItem).action}`);
  }
}

/**
 * Creates a single representation of an author.  Will include a
 * comma in the author's name if this isn't the final author of the list.
 *
 * @param author - a User who contributed to this entry
 * @param final - is this the last User in the list
 */
function Author({author, final}: {author: User; final: boolean}): JSX.Element {
  return (
    <span>
      <strong>{author.name}</strong>
      {!final && ', '}
    </span>
  );
}

/**
 * Creates a Row containing all of the authors provided
 *
 * @param authors - an array of users who helped create this entry
 * @returns a React element containing all of the authors
 */
function Authors({authors}: {authors: Array<User>}): JSX.Element {
  const authorsElements = Array.from(authors).map((author, i) => (
    <Author key={author.email} author={author} final={i >= authors.length - 1} />
  ));
  return (
    <Row>
      <Col sm={12}>
        <ReviewLabelText>Authors</ReviewLabelText>
        <span className="u-spacing-left _test-review-authors">{authorsElements}</span>
      </Col>
    </Row>
  );
}

/**
 * Creates a single representation of a reviewer.  Will include a
 * comma in the author's name if this isn't the final reviewer of the list.
 *
 * @param reviewer - a Reviewer who contributed to this entry
 * @param final - is this the last Reviewer in the list
 */
function ReviewerItem({reviewer, final}: {reviewer: ReviewerUser; final: boolean}): JSX.Element {
  const checkbox = reviewer.approved ? (
    <ApprovedCheckbox title="Approved" />
  ) : (
    <UnapprovedCheckbox title="Not approved yet" />
  );
  return (
    <span>
      {checkbox}
      {'\u00A0'}
      <strong>{reviewer.name}</strong>
      {!final && ', '}
    </span>
  );
}

/**
 * Creates a Row containing all the reviewers and the status of their review for an entry.
 * Note that legacy reviews will reference reviewers as auditors.
 *
 * @param reviewers - an array of reviewers for the entry
 * @param canEditReviewers - a boolean indicating if the user can change the reviewers on the entry
 * @param openUpdateReviewModal - a callback function called when we want to edit the reviewers on this entry
 * @returns a React element containing all the reviewers fo the entry
 */
function Reviewers({
  review,
  canEditReviewers,
  openUpdateReviewModal,
}: {
  review: ReviewModel;
  canEditReviewers: boolean;
  openUpdateReviewModal: () => void;
}): JSX.Element {
  let reviewers = review.auditors;

  const result = useBenchlingQuery<ReviewLifecycleReviewersQuery, ReviewLifecycleReviewersQueryVariables>(
    REVIEW_LIFECYCLE_REVIEWERS_QUERY,
    {
      variables: {reviewId: review.id},
    }
  );

  let hasReviewLifecycle = false;

  if (result.type === QueryResultType.SUCCESS) {
    const reviewGraphQL = result.data?.review;
    assert(
      !isInaccessible(reviewGraphQL),
      "User shouldn't be able to access a review tab if it's inaccessible"
    );
    hasReviewLifecycle = reviewGraphQL.lastUsedReviewLifecycleVersion != null;
    if (hasReviewLifecycle && reviewGraphQL.reviewers) {
      reviewers = reviewGraphQL.reviewers.map((reviewerNode) => ({
        approved: reviewerNode.reviewStatus === ReviewerStatus.FINISHED,
        ...(reviewerNode.user as unknown as User),
      }));
    }
  }
  const reviewersElements = Array.from(reviewers).map((reviewer, i) => (
    <ReviewerItem key={reviewer.email} reviewer={reviewer} final={i >= reviewers.length - 1} />
  ));

  const reviewLabelText = hasReviewLifecycle ? 'Reviewers' : 'Auditors';
  return (
    <Row>
      <Col sm={12}>
        <ReviewLabelText>{reviewLabelText}</ReviewLabelText>
        <span className="u-spacing-left _test-review-auditors">
          {reviewersElements}
          {'\u00A0\u00A0'}
          {canEditReviewers && (
            <a className="u-no-text-decoration" onClick={openUpdateReviewModal}>
              <BenchlingIcon icon="edit" />
            </a>
          )}
        </span>
      </Col>
    </Row>
  );
}

/**
 * A React element showing the recent review history of an entry.  This element
 * is stateless and relies on the controlling component to tell it which
 * history items it should show as well as providing it with some callback to
 * indicate that we want to show all history items.
 *
 * @param apiType -- a string representation of the api type of item that the
 *                   historyItem belongs to, e.g. Entry or StageEntry
 * @param historyItems -- an array of history items for the current entry of
 *                        which we may only show a subset
 * @param historyStartIndex - the index of where we should start showing in
 *                            the historyItems.  If this is 0, then we will
 *                            show all the historyItems
 * @param showAllHistory - a callback which will be called when the user
 *                         indicates they want to see all of the history items.
 * @param refetch -- a callback to refetch the review history
 * @returns a React element showing the recent reviewing history of an entry.
 *          If we have no historyItems, then we will return null
 */
export function RecentActivity({
  apiType,
  historyItems,
  historyStartIndex,
  showAllHistory,
  hasReviewLifecycle,
  reviewId,
  fileId,
  refetch,
}: {
  apiType: string;
  historyItems?: Array<HistoryItem>;
  historyStartIndex: number;
  showAllHistory: () => void;
  hasReviewLifecycle?: boolean;
  reviewId?: string;
  fileId: string;
  refetch?: () => void;
}): JSX.Element | null {
  if (!historyItems) {
    return null;
  }
  let lastFailedSnapshot = false;
  const historyElements = historyItems.slice(historyStartIndex).map((item, i) => {
    if (item.info?.snapshot != null) {
      lastFailedSnapshot = item.info.snapshot?.status === STATUS.FAILED;
    }
    return (
      <div
        className="u-small-spacing-top browser-history-activity"
        key={`${item.time}~${i}`}
        {...getTestProps({component: 'HistoryItem', element: 'reviewLog'})}
      >
        <HistoryItemIcon historyItem={item} className="icon u-small-spacing-right text-center" />
        <img className="u-small-spacing-right avatar" src={item.user.avatar_url} width={20} height={20} />
        <HistoryItemSummary historyItem={item} apiType={apiType} hasReviewLifecycle={hasReviewLifecycle} />
        <span className="pull-right faded">
          <RelativeTime datetime={item.time} />
        </span>
        {item.info && item.info.message && (
          <div className="u-small-spacing-top browser-activity-note">
            <p
              className="u-no-spacing-bottom"
              {...getTestProps({component: 'HistoryItem', element: 'reviewComment'})}
            >
              {item.info.message}
            </p>
          </div>
        )}
        {item.info && item.info.snapshot && (
          <SnapshotInfo apiType={apiType} snapshot={item.info.snapshot} showCreatedTime={true} />
        )}
      </div>
    );
  });
  if (lastFailedSnapshot && reviewId != null) {
    historyElements.push(
      <div
        className="u-small-spacing-top browser-history-activity"
        key="reset-snap"
        id="reset-snap"
        {...getTestProps({component: 'HistoryItem', element: 'reviewLog'})}
      >
        <LastSnapshotFailedIconAndSummary fileId={fileId} reviewId={reviewId} refetch={refetch} />
      </div>
    );
  }
  const showAllChangesCallToAction: JSX.Element | null =
    historyStartIndex !== 0 ? (
      <div className="browser-history-older-changes text-center">
        {`${historyStartIndex} older changes are hidden. `}
        <a onClick={showAllHistory}>Show all changes.</a>
      </div>
    ) : null;
  return (
    <Row>
      <Col sm={12}>
        <ReviewLabelText>Recent Activity</ReviewLabelText>
        {showAllChangesCallToAction}
        <div>{historyElements}</div>
      </Col>
    </Row>
  );
}

/**
 * A React component showing the review history of a particular entry.
 */
export default class Review extends React.Component<ReviewProps, ReviewState> {
  constructor(props: ReviewProps) {
    super(props);

    // We store the start index (instead of numShowing/boolean) so that adding a comment
    // appends to the end, instead of bumping a comment out of the way.
    this.state = {
      historyStartIndex:
        this.props.review && this.props.review.history
          ? Math.max(0, this.props.review.history.length - this.props.initialItemsShowing)
          : 0,
    };
  }

  static defaultProps = {
    initialItemsShowing: 3,
  };

  /**
   * Our callback function passed to the RecentActivity component that is
   * called when we want to show all activity.
   */
  showAllHistory = (): void => {
    this.setState({historyStartIndex: 0});
  };

  /**
   * This callback function will be called by the Reviewers component for when
   * we want to modify who is reviewing an entry.
   */
  openUpdateReviewModal = (): void => {
    CreateUpdateReviewModalClass.open({
      fileId: this.props.id,
      review: this.props.review,
      onUpdate: (
        _fileId: string,
        review: ReviewModel,
        filePermissions: Permissions,
        snapshotTask: OngoingTaskResponse | null
      ) => {
        this.props.onUpdate(this.props.id, review, filePermissions, snapshotTask);
      },
      isSelfReview: false,
    });
  };

  override render(): JSX.Element {
    const {review} = this.props;
    if (!review) {
      return <NoReviewInProgress linkedItemId={this.props.id} writable={this.props.permissions.writable} />;
    }
    return (
      <div>
        <Authors authors={review.authors} />
        <Reviewers
          review={review}
          // canEditReviewers is false if project has review lifecycle, otherwise it uses permissions.writable
          canEditReviewers={this.props.hasReviewLifecycle ? false : this.props.permissions.writable}
          openUpdateReviewModal={this.openUpdateReviewModal}
        />
        {BenchlingConfig.ENABLE_ELN_REVIEW_CRITERIA &&
          (this.props.permissions.canSendForReview ? (
            <ConfigureReviewCriteriaSection
              linkedItemId={this.props.id}
              writable={this.props.permissions.writable}
            />
          ) : (
            <ReviewCriteriaSection linkedItemId={this.props.id} review={review} />
          ))}
        <RecentActivity
          apiType={APIUtil.apiIdToType(this.props.id)}
          historyItems={review.history}
          historyStartIndex={this.state.historyStartIndex}
          showAllHistory={this.showAllHistory}
          hasReviewLifecycle={this.props.hasReviewLifecycle}
          reviewId={this.props.review?.id}
          fileId={this.props.id}
        />
      </div>
    );
  }
}
